import React, { useState, useMemo } from "react";
import { useNavigationEvent } from "navigation-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faTimes,
  faRefresh,
  faMoneyBillWave,
  faReceipt,
  faUtensils,
  faBagShopping,
  faMotorcycle,
  faUsers,
  faBox,
  faPercent,
  faClock,
  faCheckCircle,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import MessageModal from "./MessageModal";

// Fetcher for orders API
const ordersFetcher = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Define colors for charts
const COLORS = [
  "#FF6B35",
  "#10B981",
  "#3B82F6",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
];

// Number of latest orders to display in table
const LATEST_ORDERS_COUNT = 5;

export default function DashboardContent() {
  const { stateNavigator } = useNavigationEvent();
  const [selectedDateRange, setSelectedDateRange] = useState("today");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Build API URL with date filtering
  const getApiUrl = () => {
    let url = `${process.env.REACT_APP_BASE_URL}/api/orders`;
    let params = [];

    if (selectedDateRange === "custom" && customStartDate && customEndDate) {
      params.push(`start_date=${customStartDate}`);
      params.push(`end_date=${customEndDate}`);
    } else if (selectedDateRange === "today") {
      params.push(`period=today`);
    } else if (selectedDateRange === "week") {
      params.push(`period=week`);
    } else if (selectedDateRange === "month") {
      params.push(`period=month`);
    } else if (selectedDateRange === "year") {
      params.push(`period=year`);
    }

    if (params.length > 0) {
      url += "?" + params.join("&");
    }

    return url;
  };

  // Fetch orders
  const {
    data: ordersResponse,
    isLoading,
    error,
    mutate,
  } = useSWR(getApiUrl(), ordersFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 60000,
  });

  const allOrders = ordersResponse?.data || [];

  // Sort orders by created date (newest first) and take only the latest 5
  const latestOrders = useMemo(() => {
    return [...allOrders]
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, LATEST_ORDERS_COUNT);
  }, [allOrders]);

  const totalOrdersCount = allOrders.length;

  // Reset page when date range changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedDateRange, customStartDate, customEndDate]);

  // Split orders by status for statistics
  const paidOrders = allOrders.filter((order) => order.status === "paid");
  const receivedOrders = allOrders.filter(
    (order) => order.status === "received",
  );
  const preparingOrders = allOrders.filter(
    (order) => order.status === "preparing",
  );
  const readyOrders = allOrders.filter((order) => order.status === "ready");

  // Calculate daily sales data (uses ALL orders for accurate chart)
  const dailySalesData = useMemo(() => {
    const dailyMap = new Map();

    allOrders.forEach((order) => {
      const date = new Date(order.created).toLocaleDateString("en-GB");
      const amount = parseFloat(order.total || 0);

      if (dailyMap.has(date)) {
        dailyMap.set(date, dailyMap.get(date) + amount);
      } else {
        dailyMap.set(date, amount);
      }
    });

    return Array.from(dailyMap.entries())
      .map(([date, sales]) => ({ date, sales }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-14);
  }, [allOrders]);

  // Calculate order type distribution
  const orderTypeData = useMemo(() => {
    const types = {
      "dine-in": 0,
      takeaway: 0,
      delivery: 0,
    };

    allOrders.forEach((order) => {
      if (types[order.order_type] !== undefined) {
        types[order.order_type]++;
      }
    });

    return Object.entries(types).map(([name, value]) => ({
      name:
        name === "dine-in"
          ? "Dine In"
          : name === "takeaway"
            ? "Takeaway"
            : "Delivery",
      value,
    }));
  }, [allOrders]);

  // Calculate order status distribution
  const orderStatusData = useMemo(() => {
    return [
      { name: "Paid", value: paidOrders.length, color: "#10B981" },
      { name: "Received", value: receivedOrders.length, color: "#F59E0B" },
      { name: "Preparing", value: preparingOrders.length, color: "#3B82F6" },
      { name: "Ready", value: readyOrders.length, color: "#8B5CF6" },
    ].filter((item) => item.value > 0);
  }, [paidOrders, receivedOrders, preparingOrders, readyOrders]);

  // Calculate hourly sales pattern
  const hourlySalesData = useMemo(() => {
    const hourlyMap = new Map();
    for (let i = 0; i < 24; i++) hourlyMap.set(i, 0);

    allOrders.forEach((order) => {
      const hour = new Date(order.created).getHours();
      const amount = parseFloat(order.total || 0);
      hourlyMap.set(hour, hourlyMap.get(hour) + amount);
    });

    return Array.from(hourlyMap.entries()).map(([hour, sales]) => ({
      hour: `${hour.toString().padStart(2, "0")}:00`,
      sales,
    }));
  }, [allOrders]);

  // Calculate weekly sales trend
  const weeklySalesData = useMemo(() => {
    const weekMap = new Map();
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    days.forEach((day) => weekMap.set(day, 0));

    allOrders.forEach((order) => {
      const day = days[new Date(order.created).getDay()];
      const amount = parseFloat(order.total || 0);
      weekMap.set(day, weekMap.get(day) + amount);
    });

    return days.map((day) => ({ day, sales: weekMap.get(day) }));
  }, [allOrders]);

  // Calculate product popularity (from paid orders only)
  const productPopularity = useMemo(() => {
    const productMap = new Map();

    paidOrders.forEach((order) => {
      order.items?.forEach((item) => {
        if (productMap.has(item.name)) {
          productMap.set(item.name, productMap.get(item.name) + item.quantity);
        } else {
          productMap.set(item.name, item.quantity);
        }
      });
    });

    return Array.from(productMap.entries())
      .map(([name, quantity]) => ({ name, quantity }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);
  }, [paidOrders]);

  // Calculate statistics - FIXED: Total Revenue calculated from subtotal + service charge
  const statistics = useMemo(() => {
    if (!paidOrders.length) {
      return {
        totalSales: 0,
        totalRevenue: 0,
        totalOrders: 0,
        totalPaidOrders: 0,
        totalReceivedOrders: 0,
        totalPreparingOrders: 0,
        totalReadyOrders: 0,
        averageOrderValue: 0,
        dineInOrders: 0,
        takeawayOrders: 0,
        deliveryOrders: 0,
        totalItems: 0,
        totalGuests: 0,
        totalServiceCharge: 0,
        pendingRevenue: 0,
      };
    }

    let totalSales = 0;
    let totalServiceCharge = 0;
    let totalRevenue = 0;
    let totalItems = 0;

    // Calculate from subtotal + service charge (not from order.total)
    paidOrders.forEach((order) => {
      // Calculate subtotal (without service charge)
      const subtotal =
        order.items?.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        ) || 0;

      const scPercentage = order.service_charge_percentage || 5;
      const scAmount = subtotal * (scPercentage / 100);

      // Calculate revenue from subtotal + service charge
      const calculatedTotal = subtotal + scAmount;

      totalSales += subtotal;
      totalServiceCharge += scAmount;
      totalRevenue += calculatedTotal;

      // Calculate items sold
      const itemsCount =
        order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      totalItems += itemsCount;
    });

    const totalOrders = allOrders.length;
    const totalPaidOrders = paidOrders.length;
    const totalReceivedOrders = receivedOrders.length;
    const totalPreparingOrders = preparingOrders.length;
    const totalReadyOrders = readyOrders.length;

    const averageOrderValue =
      totalPaidOrders > 0 ? totalRevenue / totalPaidOrders : 0;

    const dineInOrders = allOrders.filter(
      (o) => o.order_type === "dine-in",
    ).length;
    const takeawayOrders = allOrders.filter(
      (o) => o.order_type === "takeaway",
    ).length;
    const deliveryOrders = allOrders.filter(
      (o) => o.order_type === "delivery",
    ).length;

    const totalGuests = allOrders.reduce((sum, order) => {
      return (
        sum +
        (order.order_type === "dine-in" && order.number_of_people
          ? order.number_of_people
          : 0)
      );
    }, 0);

    const pendingRevenue =
      receivedOrders.reduce(
        (sum, order) => sum + parseFloat(order.total || 0),
        0,
      ) +
      preparingOrders.reduce(
        (sum, order) => sum + parseFloat(order.total || 0),
        0,
      ) +
      readyOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0);

    return {
      totalSales, // Menu subtotal only
      totalRevenue, // Calculated: totalSales + totalServiceCharge
      totalOrders,
      totalPaidOrders,
      totalReceivedOrders,
      totalPreparingOrders,
      totalReadyOrders,
      averageOrderValue,
      dineInOrders,
      takeawayOrders,
      deliveryOrders,
      totalItems,
      totalGuests,
      totalServiceCharge,
      pendingRevenue,
    };
  }, [paidOrders, allOrders, receivedOrders, preparingOrders, readyOrders]);

  const handleRefresh = () => {
    mutate();
  };

  if (isLoading && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading dashboard...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (error && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Data</h3>
              <p>Unable to connect to the API. Please check your connection.</p>
              <button style={styles.retryButton} onClick={handleRefresh}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          {/* Header with Title and Filters */}
          <div style={styles.headerSection}>
            <div>
              <h1 style={styles.title}>Dashboard</h1>
              <p style={styles.subtitle}>
                Real-time sales analytics and business insights
              </p>
            </div>
          </div>

          {/* KPI CARDS - Row 1: Sales & Revenue & Order Types */}
          <div style={styles.kpiGrid}>
            {/* Total Sales (excl. service charge) */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>
                  GH₵{statistics.totalSales.toFixed(2)}
                </h3>
                <p style={styles.kpiLabel}>Total Sales (excl. service)</p>
              </div>
            </div>

            {/* Total Revenue (incl. service charge) */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>
                  GH₵{statistics.totalRevenue.toFixed(2)}
                </h3>
                <p style={styles.kpiLabel}>Total Revenue (incl. service)</p>
              </div>
            </div>

            {/* Service Charge */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faPercent}
                  style={{ color: "#FF9800", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>
                  GH₵{statistics.totalServiceCharge.toFixed(2)}
                </h3>
                <p style={styles.kpiLabel}>Service Charge</p>
                <span style={styles.kpiSubtext}>5% on dine-in orders</span>
              </div>
            </div>

            {/* Items Sold */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faBox}
                  style={{ color: "#8B5CF6", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.totalItems}</h3>
                <p style={styles.kpiLabel}>Items Sold</p>
                <span style={styles.kpiSubtext}>From paid orders only</span>
              </div>
            </div>
          </div>

          {/* KPI CARDS - Row 2: Orders & Revenue */}
          <div style={styles.kpiGrid}>
            {/* Paid Orders */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#10B981", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.totalPaidOrders}</h3>
                <p style={styles.kpiLabel}>Paid Orders</p>
                <span style={styles.kpiSubtext}>
                  Avg. GH₵{statistics.averageOrderValue.toFixed(2)} per order
                </span>
              </div>
            </div>

            {/* Total Orders */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faReceipt}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.totalOrders}</h3>
                <p style={styles.kpiLabel}>Total Orders</p>
                <span style={styles.kpiSubtext}>
                  Paid: {statistics.totalPaidOrders} | Unpaid:{" "}
                  {statistics.totalOrders - statistics.totalPaidOrders}
                </span>
              </div>
            </div>

            {/* Pending Revenue */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#EF4444", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>
                  GH₵{statistics.pendingRevenue.toFixed(2)}
                </h3>
                <p style={styles.kpiLabel}>Pending Revenue</p>
                <span style={styles.kpiSubtext}>Orders not yet paid</span>
              </div>
            </div>

            {/* Total Guests */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faUsers}
                  style={{ color: "#9C27B0", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.totalGuests}</h3>
                <p style={styles.kpiLabel}>Total Guests</p>
                <span style={styles.kpiSubtext}>Across all orders</span>
              </div>
            </div>
          </div>

          {/* KPI CARDS - Row 3: Order Types & Pending Orders */}
          <div style={styles.kpiGrid}>
            {/* Dine In Orders - MOVED TO TOP */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faUtensils}
                  style={{ color: "#FF6B35", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.dineInOrders}</h3>
                <p style={styles.kpiLabel}>Dine In Orders</p>
                <span style={styles.kpiSubtext}>
                  {statistics.totalOrders > 0
                    ? `${((statistics.dineInOrders / statistics.totalOrders) * 100).toFixed(1)}% of total`
                    : "0% of total"}
                </span>
              </div>
            </div>

            {/* Takeaway Orders - MOVED TO TOP */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faBagShopping}
                  style={{ color: "#10B981", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.takeawayOrders}</h3>
                <p style={styles.kpiLabel}>Takeaway Orders</p>
                <span style={styles.kpiSubtext}>
                  {statistics.totalOrders > 0
                    ? `${((statistics.takeawayOrders / statistics.totalOrders) * 100).toFixed(1)}% of total`
                    : "0% of total"}
                </span>
              </div>
            </div>

            {/* Delivery Orders - MOVED TO TOP */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faMotorcycle}
                  style={{ color: "#3B82F6", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>{statistics.deliveryOrders}</h3>
                <p style={styles.kpiLabel}>Delivery Orders</p>
                <span style={styles.kpiSubtext}>
                  {statistics.totalOrders > 0
                    ? `${((statistics.deliveryOrders / statistics.totalOrders) * 100).toFixed(1)}% of total`
                    : "0% of total"}
                </span>
              </div>
            </div>

            {/* Pending Orders */}
            <div style={styles.kpiCard}>
              <div style={styles.kpiIconContainer}>
                <FontAwesomeIcon
                  icon={faClock}
                  style={{ color: "#F59E0B", fontSize: "24px" }}
                />
              </div>
              <div style={styles.kpiContent}>
                <h3 style={styles.kpiValue}>
                  {statistics.totalReceivedOrders +
                    statistics.totalPreparingOrders +
                    statistics.totalReadyOrders}
                </h3>
                <p style={styles.kpiLabel}>Pending Orders</p>
                <span style={styles.kpiSubtext}>
                  Received: {statistics.totalReceivedOrders} | Preparing:{" "}
                  {statistics.totalPreparingOrders} | Ready:{" "}
                  {statistics.totalReadyOrders}
                </span>
              </div>
            </div>
          </div>

          {/* Order Status Distribution Pie Chart */}
          {orderStatusData.length > 0 && (
            <div style={styles.chartsRow}>
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>Order Status</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} orders`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>Orders by Type (All Orders)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={orderTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {orderTypeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} orders`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Charts Row: Daily Sales Trend */}
          <div style={styles.chartsRow}>
            <div style={styles.chartCard}>
              <h3 style={styles.chartTitle}>
                Daily Sales Trend (incl. service)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailySalesData}>
                  <defs>
                    <linearGradient
                      id="salesGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => `GH₵${value.toFixed(2)}`} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="#FF6B35"
                    fill="url(#salesGradient)"
                    name="Sales"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div style={styles.chartCard}>
              <h3 style={styles.chartTitle}>Sales by Hour</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={hourlySalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip formatter={(value) => `GH₵${value.toFixed(2)}`} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    stroke="#10B981"
                    name="Sales"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row: Weekly Pattern & Top Products */}
          <div style={styles.chartsRow}>
            <div style={styles.chartCard}>
              <h3 style={styles.chartTitle}>Weekly Sales Pattern</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklySalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip formatter={(value) => `GH₵${value.toFixed(2)}`} />
                  <Legend />
                  <Bar
                    dataKey="sales"
                    fill="#3B82F6"
                    name="Sales"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={styles.chartCard}>
              <h3 style={styles.chartTitle}>Top 10 Products</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={productPopularity} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={120} />
                  <Tooltip formatter={(value) => `${value} units sold`} />
                  <Legend />
                  <Bar
                    dataKey="quantity"
                    fill="#FF6B35"
                    name="Quantity Sold"
                    radius={[0, 8, 8, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* LATEST ORDERS TABLE - Only 5 latest orders */}
          <div style={styles.tableCard}>
            <div style={styles.tableHeader}>
              <h3 style={styles.tableTitle}>Latest Orders</h3>
              <span style={styles.tableCount}>
                Showing {Math.min(latestOrders.length, LATEST_ORDERS_COUNT)} of{" "}
                {totalOrdersCount} total orders
              </span>
            </div>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Order #</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Time</th>
                    <th style={styles.th}>Customer</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Table</th>
                    <th style={styles.th}>Guests</th>
                    <th style={styles.th}>Items</th>
                    <th style={styles.th}>Subtotal</th>
                    <th style={styles.th}>Service Charge</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {latestOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan="12"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        No orders found for the selected date range
                      </td>
                    </tr>
                  ) : (
                    latestOrders.map((order) => {
                      const subtotal =
                        order.items?.reduce(
                          (sum, item) => sum + item.price * item.quantity,
                          0,
                        ) || 0;
                      const scPercentage = order.service_charge_percentage || 5;
                      const serviceCharge = subtotal * (scPercentage / 100);
                      const totalItems =
                        order.items?.reduce(
                          (sum, item) => sum + item.quantity,
                          0,
                        ) || 0;
                      const orderType =
                        order.order_type === "dine-in"
                          ? "Dine In"
                          : order.order_type === "takeaway"
                            ? "Takeaway"
                            : "Delivery";

                      return (
                        <tr key={order.id} style={styles.tableRow}>
                          <td style={styles.td}>#{order.id}</td>
                          <td style={styles.td}>
                            {new Date(order.created).toLocaleDateString(
                              "en-GB",
                            )}
                          </td>
                          <td style={styles.td}>
                            {new Date(order.created).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td style={styles.td}>
                            <strong>{order.guest_name || "Guest"}</strong>
                          </td>
                          <td style={styles.td}>{orderType}</td>
                          <td style={styles.td}>{order.table_number || "-"}</td>
                          <td style={styles.td}>
                            {order.number_of_people || "-"}
                          </td>
                          <td style={styles.td}>{totalItems}</td>
                          <td style={styles.td}>GH₵{subtotal.toFixed(2)}</td>
                          <td style={styles.td}>
                            {serviceCharge > 0
                              ? `GH₵${serviceCharge.toFixed(2)}`
                              : "-"}
                          </td>
                          <td style={styles.td}>
                            <strong>
                              GH₵{parseFloat(order.total).toFixed(2)}
                            </strong>
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                backgroundColor:
                                  order.status === "paid"
                                    ? "#E8F5E9"
                                    : "#FFF3E0",
                                color:
                                  order.status === "paid"
                                    ? "#2E7D32"
                                    : "#E65100",
                              }}
                            >
                              {order.status === "paid" ? "Paid" : order.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* REMOVED: Old Order Type Stats Cards at the bottom - Now moved to top */}
        </div>
      </div>
      <DashboardContentFooter />

      {modalError && (
        <MessageModal
          leftIcon={faTimes}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  headerSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1F2937",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#6B7280",
    margin: "4px 0 0 0",
  },
  filterControls: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  dateSelect: {
    padding: "10px 16px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "14px",
    cursor: "pointer",
  },
  customDateRange: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  dateInput: {
    padding: "6px 10px",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    fontSize: "13px",
  },
  refreshButton: {
    padding: "10px 16px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "24px",
  },
  kpiCard: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  kpiIconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    backgroundColor: "#F3F4F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  kpiContent: {
    flex: 1,
  },
  kpiValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1F2937",
    margin: 0,
  },
  kpiLabel: {
    fontSize: "13px",
    color: "#6B7280",
    margin: "4px 0 0 0",
  },
  kpiSubtext: {
    fontSize: "11px",
    color: "#9CA3AF",
    marginTop: "4px",
    display: "block",
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "24px",
  },
  chartCard: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  chartTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1F2937",
    margin: "0 0 16px 0",
  },
  tableCard: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "24px",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #E5E7EB",
  },
  tableTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    margin: 0,
  },
  tableCount: {
    fontSize: "13px",
    color: "#6B7280",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1200px",
  },
  th: {
    textAlign: "left",
    padding: "12px",
    backgroundColor: "#F9FAFB",
    color: "#374151",
    fontWeight: "600",
    fontSize: "13px",
    borderBottom: "1px solid #E5E7EB",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #F0F0F0",
    fontSize: "13px",
    color: "#1F2937",
  },
  tableRow: {
    transition: "background-color 0.2s",
  },
  statusBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
  },
  // REMOVED: orderTypeStats styles (no longer needed at bottom)
};
